import React, { useState, useEffect } from "react";
import { httpClient, authFetch } from "../../HttpClient";
import { uploadFileToBlob } from "../../utils/uploadFileToBlob";
import { showStatusToast } from "../../components/ui/Toast";
import "./Lead.css";
import { IoSearchSharp } from "react-icons/io5";
import { useEtatGlobal } from "../EtatGlobal.js";

import { dialog } from "../../components/ui/Dialog";
import {
  FileText,
  ChevronDown,
  ChevronRight,
  Loader2,
  Check,
  User,
  Building2,
  Calendar,
  Tag,
} from "lucide-react";

function FormLead({ onCancel }) {
  const { utilisateur, setLeads, fetchLeads } = useEtatGlobal();
  const [hasRDV, setHasRDV] = useState(false);
  const [numSiret, setNumSiret] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [ChaqueAssocieDocument, setChaqueAssocieDocument] = useState(false);
  const [formData, setFormData] = useState({
    userId: "",
    civilite: "",
    nom: "",
    prenom: "",
    provenance: "Autre",
    adresse: "",
    formeSociale: "",
    societeNom: "",
    sirenSociete: "",
    tel: "",
    mail: "",
    dateRDV: "",
    heureRDV: "",
    lieuRDV: "",
    objetRDV: "",
    cabinetId: utilisateur?.userCabinets?.[0]?.cabinetId ?? "",
  });

  const [files, setFiles] = useState({
    bilanDocument: null,
    identityDocument: null,
    kbisDocument: null,
    statusDocument: null,
    pieceIdentite: null,
  });

  const [users, setUsers] = useState([]);
  const [cabinets, setCabinets] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSearchSiret = async () => {
    if (numSiret.length !== 14) {
      dialog.alert("Le numéro SIRET doit contenir 14 chiffres.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await authFetch(
        `https://backend-myalfa.vercel.app/api/siret/${numSiret}`
      );
      const data = await response.json();
      if (data.success) {
        const {
          siren,
          denominationUniteLegale,
          sexeUniteLegale,
          adresse,
          nomUniteLegale,
          prenomUsuelUniteLegale,
        } = data;

        setFormData((prev) => ({
          ...prev,
          sirenSociete: siren || "",
          societeNom: denominationUniteLegale || "",
          adresse: adresse
            ? `${adresse.numeroVoie || ""} ${adresse.typeVoie || ""} ${
                adresse.libelleVoie || ""
              }, ${adresse.codePostal || ""} ${adresse.libelleCommune || ""}`
            : "",
          nom: nomUniteLegale || "",
          prenom: prenomUsuelUniteLegale || "",
          civilite: sexeUniteLegale === "F" ? "Madame" : "Monsieur",
        }));
      } else {
        dialog.alert("Aucune donnée trouvée pour ce numéro SIRET.");
      }
    } catch (error) {
      console.error("Erreur lors de la recherche SIRET :", error);
      dialog.alert("Erreur lors de la recherche. Vérifiez le serveur.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNumSiretChange = (e) => {
    setNumSiret(e.target.value);
  };

  useEffect(() => {
    httpClient
      .get("https://backend-myalfa.vercel.app/api/users")
      .then((response) => setUsers(response.data))
      .catch((error) =>
        console.error("Erreur chargement utilisateurs :", error)
      );
    httpClient
      .get("https://backend-myalfa.vercel.app/api/cabinets")
      .then((response) => setCabinets(response.data))
      .catch((error) => console.error("Erreur chargement cabinets :", error));
  }, []);

  useEffect(() => {
    if (!utilisateur) return;
    setFormData((prev) => ({
      ...prev,
      cabinetId:
        prev.cabinetId || utilisateur?.userCabinets?.[0]?.cabinetId || "",
    }));
  }, [utilisateur]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFiles((prev) => ({ ...prev, [name]: files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const specificite = hasRDV ? "rdv" : "contact";
    const selectedUser =
      users.find((u) => String(u.id) === String(formData.userId)) || null;

    // UI instantanée : l'overlay se ferme et une carte provisoire apparaît
    // tout de suite dans la bonne colonne ("Prise de contact" ou "RDV à
    // venir") ; la création réelle se fait en arrière-plan
    const tempId = `temp-${Date.now()}`;
    const optimisticLead = {
      ...formData,
      id: tempId,
      specificite,
      lastspecificite: specificite,
      status: null,
      dateRDV: hasRDV ? formData.dateRDV : "",
      heureRDV: hasRDV ? formData.heureRDV : "",
      lieuRDV: hasRDV ? formData.lieuRDV : "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      user: selectedUser,
    };
    setLeads((prev) => [optimisticLead, ...prev]);
    if (typeof onCancel === "function") {
      onCancel();
    }

    // Upload direct navigateur → Blob pour les documents joints (contourne
    // la limite 4,5 Mo) : un toast informe qu'un envoi de fichier — parfois
    // long selon sa taille — est en cours, sans bloquer la fermeture déjà
    // optimiste de l'overlay.
    const data = { ...formData, specificite, lastspecificite: specificite };

    if (!hasRDV) {
      delete data.dateRDV;
      delete data.heureRDV;
      delete data.lieuRDV;
    }

    const fileEntries = Object.entries(files).filter(([, file]) => file);
    if (fileEntries.length > 0) {
      const toast = showStatusToast({ status: "loading", titre: "Envoi du document...", message: "0 %" });
      try {
        for (const [key, file] of fileEntries) {
          const url = await uploadFileToBlob(
            file,
            "https://backend-myalfa.vercel.app/api/formleads/upload-token",
            ({ percentage }) =>
              toast.update({ status: "loading", titre: "Envoi du document...", message: `${Math.round(percentage)} %` })
          );
          data[`${key}Url`] = url;
        }
        toast.update({ status: "success", titre: "Document envoyé", message: "" });
      } catch (err) {
        toast.update({ status: "error", titre: "Échec de l'envoi du document", message: err.message });
      }
    }

    if (formData.civilite && formData.nom.trim() && formData.prenom.trim()) {
      const fdAnnuaire = new FormData();
      fdAnnuaire.append("nom", formData.nom);
      fdAnnuaire.append("prenoms", formData.prenom);
      fdAnnuaire.append("provenance", formData.provenance || "Autre");
      fdAnnuaire.append("clientIds", JSON.stringify([]));
      httpClient
        .post("https://backend-myalfa.vercel.app/api/gi/annuaire", fdAnnuaire, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .catch((err) => console.error("Erreur création annuaire :", err));
    }

    try {
      const response = await httpClient.post(
        "https://backend-myalfa.vercel.app/api/formleads",
        data
      );

      console.log("FormLead envoyé");

      if (response.status === 201 && response.data && response.data.id) {
        const idLead = response.data.id;
        console.log(" ---> ID du lead reçu depuis le backend :", idLead);

        // Remplace la carte provisoire par le lead réel (vrai id, documents)
        setLeads((prev) =>
          prev.map((l) =>
            l.id === tempId
              ? { ...optimisticLead, ...response.data, user: selectedUser }
              : l
          )
        );

        if (hasRDV) {
          const dateObj = new Date(formData.dateRDV);
          const localeDate = dateObj.toLocaleDateString("fr-FR", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          });
          const formattedDate =
            localeDate.charAt(0).toUpperCase() + localeDate.slice(1);
          const formattedTime = formData.heureRDV
            ? formData.heureRDV.replace(":", "h")
            : "";

          const emailPayload = {
            signer: formData.mail,
            nom: formData.nom,
            prenom: formData.prenom,
            dateRDV: formattedDate,
            lieuRDV: formData.lieuRDV,
            heureRDV: formattedTime,
            userName: selectedUser?.name || "",
            userLastName: selectedUser?.lastName || "",
            civilite: selectedUser?.civilite || "Monsieur",
            userMail: selectedUser?.email || " ",
            idLead: String(idLead),
          };

          try {
            const emailResponse = await authFetch(
              "https://backend-myalfa.vercel.app/api/send-rdv-email",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(emailPayload),
              }
            );
            if (!emailResponse.ok)
              throw new Error(`Statut HTTP ${emailResponse.status}`);
            console.log("Email RDV envoyé via fetch");
          } catch (emailError) {
            console.error("Erreur envoi email RDV via fetch :", emailError);
          }
        }

        // Resynchronise uniquement les leads (bien plus léger que fetchAll)
        fetchLeads();
      } else {
        throw new Error("Réponse inattendue de l'API formleads");
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi du formulaire :", error);
      // Échec : on retire la carte provisoire
      setLeads((prev) => prev.filter((l) => l.id !== tempId));
      dialog.alert("Erreur lors de la création du prospect.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = () =>
    `h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all placeholder:text-slate-300`;

  const selectClass = () =>
    `h-10 w-full rounded-lg bg-white px-3 text-sm border border-gray-200 outline-none focus:border-[#9B1B5D]/40 transition-all text-slate-700`;

  return (
    <div className="flex flex-col h-full max-h-[90vh] pt-5">
      {/* En-tête */}
      <div className="flex items-center justify-between px-6 pb-4 border-b border-slate-100 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#9B1B5D] flex items-center justify-center">
            <Tag className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 leading-tight">
              Ajouter un Prospect
            </h2>
            <p className="text-xs text-slate-400">Créer un nouveau LEAD</p>
          </div>
        </div>
      </div>

      {/* Corps du formulaire */}
      <form
        onSubmit={handleSubmit}
        encType="multipart/form-data"
        className="flex-1 overflow-y-auto px-6 py-4 space-y-4"
      >
        {/* ── Personne ou Contact ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <User className="w-4 h-4 text-[#9B1B5D]" />
            <span className="text-sm font-semibold text-slate-700">
              Personne ou Contact
            </span>
            <span className="text-xs text-[#9B1B5D] italic ml-1">
              Renseigne l'Annuaire
            </span>
          </div>

          <div className="p-4 space-y-3">
            {/* Civilité / Nom / Prénom */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Civilité
                </label>
                <select
                  name="civilite"
                  onChange={handleInputChange}
                  value={formData.civilite}
                  required
                  className={selectClass(formData.civilite)}
                >
                  <option value="">— Civilité —</option>
                  <option value="M.">Monsieur</option>
                  <option value="Mme.">Madame</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Nom
                </label>
                <input
                  name="nom"
                  value={formData.nom}
                  placeholder="Nom"
                  onChange={handleInputChange}
                  required
                  className={inputClass(formData.nom)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Prénom
                </label>
                <input
                  name="prenom"
                  value={formData.prenom}
                  placeholder="Prénom"
                  onChange={handleInputChange}
                  required
                  className={inputClass(formData.prenom)}
                />
              </div>
            </div>

            {/* Téléphone / Email */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Téléphone
                </label>
                <input
                  name="tel"
                  value={formData.tel}
                  placeholder="Téléphone"
                  onChange={handleInputChange}
                  required
                  className={inputClass(formData.tel)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Email
                </label>
                <input
                  name="mail"
                  value={formData.mail}
                  placeholder="Email"
                  onChange={handleInputChange}
                  required
                  className={inputClass(formData.mail)}
                />
              </div>
            </div>

            {/* Provenance */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Provenance du prospect
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                {["Site", "Recommandation", "Achalandage", "Autre"].map(
                  (opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, provenance: opt }))
                      }
                      className={`h-8 px-3 rounded-lg text-xs font-medium border transition-colors ${
                        formData.provenance === opt
                          ? "bg-[#9B1B5D] text-white border-[#9B1B5D]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-[#9B1B5D]/40"
                      }`}
                    >
                      {opt}
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Entreprise (si existante) ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#9B1B5D]" />
              <span className="text-sm font-semibold text-slate-700">
                Entreprise
              </span>
              <span className="text-xs text-slate-400 italic">
                (si existante)
              </span>
            </div>
            {/* Recherche SIRET */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={numSiret}
                onChange={handleNumSiretChange}
                placeholder="Recherche SIRET"
                className="h-8 w-40 px-2.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-[#9B1B5D]/40 bg-white"
              />
              <button
                type="button"
                onClick={handleSearchSiret}
                disabled={isLoading}
                className="h-8 w-8 flex items-center justify-center bg-[#9B1B5D] text-white rounded-lg hover:bg-[#7A1548] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                  <IoSearchSharp size={14} />
                )}
              </button>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {/* Forme Sociale / Dénomination / SIREN */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Forme Sociale
                </label>
                <select
                  name="formeSociale"
                  onChange={handleInputChange}
                  value={formData.formeSociale}
                  required
                  className={selectClass(formData.formeSociale)}
                >
                  <option value="">Forme Sociale</option>
                  {[
                    "E.I",
                    "E.I.R.L",
                    "EARL",
                    "EURL",
                    "SARL",
                    "SASU",
                    "SAS",
                    "SCI",
                    "SCCV",
                    "LMNP",
                    "GFA",
                    "SC",
                    "SCP",
                    "SEL",
                    "N/A",
                  ].map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Dénomination Sociale
                </label>
                <input
                  name="societeNom"
                  value={formData.societeNom}
                  placeholder="Dénomination Sociale"
                  onChange={handleInputChange}
                  required
                  className={inputClass(formData.societeNom)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  SIREN
                </label>
                <input
                  name="sirenSociete"
                  value={formData.sirenSociete}
                  placeholder="SIREN"
                  onChange={handleInputChange}
                  required
                  className={inputClass(formData.sirenSociete)}
                />
              </div>
            </div>

            {/* Adresse */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Adresse
              </label>
              <input
                name="adresse"
                value={formData.adresse}
                placeholder="Adresse de l'entreprise"
                onChange={handleInputChange}
                required
                className={inputClass(formData.adresse)}
              />
            </div>
          </div>
        </div>

        {/* ── Prise de RDV ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#9B1B5D]" />
              <span className="text-sm font-semibold text-slate-700">
                Prise de RDV
              </span>
            </div>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hasRDV}
                onChange={() => setHasRDV(!hasRDV)}
                className="accent-[#9B1B5D] w-3.5 h-3.5"
              />
              Souhaite un RDV
            </label>
          </div>

          <div className="p-4 space-y-3">
            {/* Date / Heure (visible seulement si RDV coché) */}
            {hasRDV && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Date de rendez-vous
                  </label>
                  <input
                    name="dateRDV"
                    type="date"
                    onChange={handleInputChange}
                    required={hasRDV}
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm bg-white outline-none focus:border-[#9B1B5D]/40 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">
                    Heure de rendez-vous
                  </label>
                  <input
                    name="heureRDV"
                    type="time"
                    onChange={handleInputChange}
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm bg-white outline-none focus:border-[#9B1B5D]/40 transition-all"
                  />
                </div>
              </div>
            )}

            {/* Site / Personne chez Alfa */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Site
                </label>
                <select
                  name="lieuRDV"
                  onChange={handleInputChange}
                  required={hasRDV}
                  className={selectClass(formData.lieuRDV)}
                >
                  <option value="">— Site —</option>
                  <option value="27 rue Vallon Hoarau, LE TAMPON (97430)">
                    27 rue Vallon Hoarau, LE TAMPON (97430)
                  </option>
                  <option value="19 chemin du Château d'Eau, Espace Turquoise n° 23, SAINT-PIERRE (97410)">
                    19 ch. Château d'Eau, Espace Turquoise, ST-PIERRE (97410)
                  </option>
                  <option value="RDV en ligne">RDV en ligne</option>
                  <option value="Rendez-vous téléphonique">Rendez-vous téléphonique</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Personne chez Alfa
                </label>
                <select
                  name="userId"
                  value={formData.userId}
                  onChange={handleInputChange}
                  required
                  className={selectClass(formData.userId)}
                >
                  <option value="">— Collaborateur —</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Mention RDV en ligne */}
            {formData.lieuRDV === "RDV en ligne" && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#9B1B5D]/5 border border-[#9B1B5D]/20 text-xs text-[#9B1B5D]">
                <Check className="w-3.5 h-3.5 shrink-0" />
                Un lien de connexion vous sera envoyé avant le rendez-vous.
              </div>
            )}

            {/* Mention RDV téléphonique */}
            {formData.lieuRDV === "Rendez-vous téléphonique" && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#9B1B5D]/5 border border-[#9B1B5D]/20 text-xs text-[#9B1B5D]">
                <Check className="w-3.5 h-3.5 shrink-0" />
                Le collaborateur appellera le prospect à l'heure du rendez-vous.
              </div>
            )}

            {/* Cabinet (accès multi-cabinets) */}
            {utilisateur?.allCabinetsAccess && (
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">
                  Cabinet
                </label>
                <select
                  name="cabinetId"
                  value={formData.cabinetId}
                  onChange={handleInputChange}
                  className={selectClass(formData.cabinetId)}
                >
                  <option value="">Cabinet</option>
                  {cabinets.map((cab) => (
                    <option key={cab.id} value={cab.id}>
                      {cab.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Objet */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1">
                Objet
              </label>
              <textarea
                name="objetRDV"
                rows={3}
                value={formData.objetRDV}
                onChange={handleInputChange}
                placeholder="Objet du Rendez-vous / Prise de contact..."
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white outline-none focus:border-[#9B1B5D]/40 transition-all resize-none"
              />
            </div>
          </div>
        </div>

        {/* ── Documents associés ── */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setChaqueAssocieDocument(!ChaqueAssocieDocument)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#9B1B5D]" />
              <span className="text-sm font-semibold text-slate-700">
                Documents associés
              </span>
            </div>
            {ChaqueAssocieDocument ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
          </button>

          {ChaqueAssocieDocument && (
            <div className="p-4 space-y-3">
              {[
                { name: "bilanDocument", label: "Bilan comptable" },
                { name: "identityDocument", label: "Justificatif d'identité" },
                { name: "kbisDocument", label: "Extrait Kbis" },
                { name: "statusDocument", label: "Statuts de l'entreprise" },
                {
                  name: "pieceIdentite",
                  label: "Pièce d'identité du représentant légal",
                },
              ].map(({ name, label }) => (
                <div
                  key={name}
                  className="flex items-center justify-between gap-4"
                >
                  <span className="text-xs text-slate-600 w-48 shrink-0">
                    {label}
                  </span>
                  <input
                    name={name}
                    type="file"
                    onChange={handleFileChange}
                    className="text-xs text-slate-500 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#9B1B5D]/10 file:text-[#9B1B5D] hover:file:bg-[#9B1B5D]/20 cursor-pointer"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

      </form>

      {/* Pied de page fixe */}
      <div className="shrink-0 flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-white">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 h-10 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          form="form-lead"
          disabled={isSubmitting}
          onClick={handleSubmit}
          className="flex items-center gap-2 px-8 h-10 bg-[#9B1B5D] hover:bg-[#7A1548] text-white text-sm font-medium rounded-lg shadow-sm shadow-[#9B1B5D]/20 transition-colors disabled:opacity-60"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Création en cours...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Créer le Prospect
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default FormLead;
